/* =============================================================================
 * M.M.LaFleur — gift card progress bar in the cart drawer (v4.7.9).
 *
 * WHERE IT SHOWS (Oct 22): a second bar in the top band of the drawer, right
 * under "complimentary shipping". The slot is [data-mm-cart-gc] in
 * snippets/mm_cart_contents.liquid; that file is re-rendered on every cart
 * change, and mm-cart.js fires `mm:cart:rendered` straight after each swap, so
 * the bar is redrawn in the same tick (no flicker). Until Oct 22 it was a box
 * above Secure checkout; that slot is gone.
 *
 * SETTINGS: Online Store > Themes > Customize > Theme settings (gear icon) >
 * M.M.LaFleur > "Cart gift card promo". mm_cart_drawer.liquid prints them as
 * <script id="mm-cart-promo-settings">. The older "Cart gift card promo"
 * section (sections/mm_cart_promo.liquid) is only read if that node is missing.
 *
 * WINDOW: shows only while now is between Starts and Ends. Those are entered
 * as New York wall-clock times ("2026-10-23 00:00"); daylight saving is worked
 * out here. A full ISO string with an offset also works.
 * Preview before the window: ?mm_promo_preview=1 on any URL (kept for the
 * browser tab), ?mm_promo_preview=0 to clear.
 *
 * SPEND: cart total after line and cart-level discounts, before tax and
 * shipping, minus gift card lines (promoTotal in the drawer state). Falls back
 * to /cart.js if the state is missing.
 *
 * ONE BAR (Oct 22, later): while the promo runs, shipping and the gift card
 * share one bar. It runs from $0 to the gift card threshold, with a marker at
 * the free shipping threshold (Theme settings > Free shipping threshold): ink
 * up to that marker, rust after it, both milestones labelled underneath. The
 * separate shipping bar is hidden by CSS while this one is showing, and comes
 * back on its own outside the promo window. Only combined when the shipping
 * threshold is above 0 and below the gift card threshold.
 *
 * LABELS (Oct 23, v4.7.10): the two milestone labels under the bar are a flex
 * row, not two absolutely placed spans, because at $400 / $500 the markers are
 * 80% / 100% apart and on a phone "Shipping" was drawn on top of "$100 gift
 * card". The shipping label ends at its marker (--mm-gc-label-at) and slides
 * left when the gift card label needs the room; it can never overlap it. CSS
 * in snippets/mm_cart_drawer.liquid. Pre-change copy:
 * assets/archive--mm-cart-promo.js.pre-labels.txt
 *
 * INSIDER EVENT (Oct 22, later): $100 for every $500. With "repeat" on (the
 * default; only "repeat": false in the settings JSON turns it off) one card
 * is earned per full threshold: under the first one the shared shipping bar shows as before;
 * past it, the bar runs from the last card earned to the next and the
 * message counts what has been earned ([earned]). The cart only sees this
 * cart, so orders added up across the three days are not reflected here.
 *
 * Message only. Nothing here adds a gift card to the cart.
 * ========================================================================== */

(function () {
  "use strict";

  var PREVIEW_KEY = "mm_promo_preview";
  var cfg = null;
  var lastTotals = null;

  /* Used only if neither config node is on the page. */
  var DEFAULTS = {
    enable: true,
    repeat: true,
    threshold: 500,
    reward: 100,
    currency: "$",
    start: "2026-10-23 00:00",
    end: "2026-10-25 23:59",
    belowText: "You're [remaining] away from a [reward] gift card.",
    reachedText: "🎁 You've earned [earned]. Spend [remaining] more for another [reward].",
    termsText: "Orders Oct 23 to 25 add up. Emailed Nov 2, use by Nov 20. Orders that earn one are exchange-only. Terms apply.",
    termsUrl: "/pages/gift-card-offer",
    countMode: "merch_after_discounts",
    shippingThreshold: 400
  };

  function readConfig() {
    if (cfg) return cfg;
    var node = document.getElementById("mm-cart-promo-settings") ||
               document.getElementById("mm-cart-promo-config");
    if (!node) { cfg = DEFAULTS; return cfg; }
    try {
      cfg = JSON.parse(node.textContent || "{}");
    } catch (err) {
      cfg = DEFAULTS;
    }
    return cfg;
  }

  /* --- preview flag -------------------------------------------------------- */

  function previewOn() {
    var on = false;
    try {
      var q = new URLSearchParams(window.location.search).get(PREVIEW_KEY);
      if (q === "1") window.sessionStorage.setItem(PREVIEW_KEY, "1");
      if (q === "0") window.sessionStorage.removeItem(PREVIEW_KEY);
      on = window.sessionStorage.getItem(PREVIEW_KEY) === "1";
    } catch (err) {
      try { on = new URLSearchParams(window.location.search).get(PREVIEW_KEY) === "1"; }
      catch (e2) { on = false; }
    }
    return on;
  }

  /* --- dates: New York wall clock -> epoch ms --------------------------------- */

  /* Minutes New York is ahead of UTC at instant ms (-240 in EDT, -300 in EST). */
  function nyOffset(ms) {
    try {
      var parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York", hourCycle: "h23",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit"
      }).formatToParts(new Date(ms));
      var o = {};
      parts.forEach(function (p) { o[p.type] = p.value; });
      var asUtc = Date.UTC(+o.year, +o.month - 1, +o.day, +o.hour % 24, +o.minute, +o.second);
      return Math.round((asUtc - ms) / 60000);
    } catch (err) {
      return -240;
    }
  }

  function toEpoch(value, isEnd) {
    var s = String(value == null ? "" : value).trim();
    if (!s) return NaN;
    /* Full ISO with an offset or Z: trust it as written. */
    if (/T\d{1,2}:\d{2}.*(Z|[+-]\d{2}:?\d{2})$/i.test(s)) return Date.parse(s);
    var m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (!m) return Date.parse(s);
    var hasTime = m[4] != null;
    var h = hasTime ? +m[4] : (isEnd ? 23 : 0);
    var mi = hasTime ? +m[5] : (isEnd ? 59 : 0);
    var se = m[6] != null ? +m[6] : (isEnd ? 59 : 0);
    var wall = Date.UTC(+m[1], +m[2] - 1, +m[3], h, mi, se);
    var t = wall - nyOffset(wall) * 60000;
    var off2 = nyOffset(t);
    t = wall - off2 * 60000;
    return t;
  }

  function inWindow(c) {
    if (previewOn()) return true;
    var start = toEpoch(c.start, false);
    var end = toEpoch(c.end, true);
    if (isNaN(start) || isNaN(end)) return false;
    var now = Date.now();
    return now >= start && now <= end;
  }

  /* --- money ---------------------------------------------------------------- */

  function dollarsToCents(v) {
    var n = parseFloat(String(v == null ? "" : v).replace(/[^0-9.]/g, ""));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  /* $X with no cents when whole, $X.YY otherwise, thousands separated. */
  function money(cents, symbol) {
    var c = Math.max(0, Math.round(cents));
    var whole = Math.floor(c / 100);
    var frac = c % 100;
    var s = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    if (frac) s += "." + (frac < 10 ? "0" : "") + frac;
    return (symbol || "$") + s;
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  function fill(template, tokens) {
    var out = esc(template);
    Object.keys(tokens).forEach(function (k) {
      out = out.split("[" + k + "]").join(tokens[k]);
    });
    return out;
  }

  /* --- cart totals ------------------------------------------------------------ */

  function isGiftCardLine(item) {
    if (!item) return false;
    if (item.gift_card === true) return true;
    var t = String(item.product_type || "").trim().toLowerCase();
    return t === "gift card";
  }

  function totalsFromState(state) {
    if (!state || typeof state.promoTotal !== "number") return null;
    return {
      after: state.promoTotal,
      before: typeof state.originalTotal === "number"
        ? Math.max(0, state.originalTotal - (state.giftCardTotal || 0))
        : state.promoTotal,
      ship: typeof state.subtotal === "number" ? state.subtotal : state.promoTotal,
      empty: !!state.empty
    };
  }

  function totalsFromCartJs(cart) {
    var gc = 0;
    var gcOriginal = 0;
    (cart.items || []).forEach(function (it) {
      if (isGiftCardLine(it)) {
        gc += it.final_line_price || 0;
        gcOriginal += it.original_line_price || 0;
      }
    });
    return {
      after: Math.max(0, (cart.total_price || 0) - gc),
      before: Math.max(0, (cart.original_total_price || cart.items_subtotal_price || 0) - gcOriginal),
      ship: cart.total_price || 0,
      empty: !cart.item_count
    };
  }

  function fetchTotals() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return fetch(root + "cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(totalsFromCartJs)
      .catch(function () { return null; });
  }

  /* --- render ----------------------------------------------------------------- */

  function slot() {
    return document.querySelector("[data-mm-cart] [data-mm-cart-gc]");
  }

  function clear() {
    var s = slot();
    if (!s) return;
    s.innerHTML = "";
    s.hidden = true;
    s.removeAttribute("data-combined");
  }

  function aria(spend, threshold, reward, sym) {
    return ' aria-label="Progress toward the ' + esc(money(reward, sym)) + ' gift card"' +
      ' aria-valuemin="0" aria-valuemax="' + Math.round(threshold / 100) + '"' +
      ' aria-valuenow="' + Math.min(Math.round(threshold / 100), Math.floor(spend / 100)) + '"' +
      ' aria-valuetext="' + esc(money(spend, sym)) + " of " + esc(money(threshold, sym)) + '"';
  }

  function render(totals) {
    var s = slot();
    var c = readConfig();
    if (!s) return;
    if (!c || c.enable !== true || !totals || totals.empty || !inWindow(c)) { clear(); return; }

    var sym = c.currency || "$";
    var threshold = dollarsToCents(c.threshold);
    var reward = dollarsToCents(c.reward);
    if (threshold <= 0 || reward <= 0) { clear(); return; }

    var spend = c.countMode === "merch_before_discounts" ? totals.before : totals.after;
    spend = Math.max(0, spend || 0);

    /* Tiers. With repeat on, one card per full threshold and the bar counts
       on from the last one earned to the next; with it off, one card. */
    var repeat = c.repeat !== false;
    var tier = repeat ? Math.floor(spend / threshold) : (spend >= threshold ? 1 : 0);
    var reached = tier >= 1;
    var base = repeat ? tier * threshold : 0;
    var target = repeat ? (tier + 1) * threshold : threshold;
    var remaining = (!repeat && reached) ? 0 : Math.max(0, target - spend);
    var pct = (!repeat && reached) ? 100 : Math.min(100, Math.round(((spend - base) / (target - base)) * 1000) / 10);
    var earned = tier === 1 ? "a " + money(reward, sym) + " gift card" : money(tier * reward, sym) + " in gift cards";

    var tokens = {
      remaining: "<strong>" + esc(money(remaining, sym)) + "</strong>",
      reward: esc(money(reward, sym)),
      threshold: esc(money(threshold, sym)),
      earned: esc(earned)
    };

    var shipT = dollarsToCents(c.shippingThreshold);
    var combine = shipT > 0 && shipT < threshold;
    var html = "";

    if (!combine) {
      html += '<p class="mm-cart-gc-msg">' +
        fill(reached ? (c.reachedText || "") : (c.belowText || ""), tokens) + "</p>";
      html += '<div class="mm-cart-progress-track" role="progressbar"' + aria(spend - base, target - base, reward, sym) + ">" +
        '<span class="mm-cart-progress-fill" style="width:' + pct + '%"></span></div>';
    } else if (reached && repeat) {
      /* Past the first card: shipping is long done, so the bar shows the run
         from the last card earned to the next one, all in the accent colour. */
      html += '<p class="mm-cart-gc-msg">' + fill(c.reachedText || "", tokens) + " Complimentary shipping included.</p>";
      html += '<div class="mm-cart-gc-bar">' +
        '<div class="mm-cart-progress-track" role="progressbar"' + aria(spend - base, target - base, reward, sym) + ">" +
        '<span class="mm-cart-progress-fill" style="width:' + pct + '%;--mm-gc-ship-at:0%"></span></div>' +
        '<span class="mm-cart-gc-mark mm-cart-gc-mark--end" data-done="false" aria-hidden="true"></span>' +
        "</div>";
      html += '<div class="mm-cart-gc-labels" aria-hidden="true">' +
        '<span class="mm-cart-gc-label" style="left:0;transform:none;text-align:left"><b>' + esc(money(tier * reward, sym)) + " earned</b><br>" +
        esc(money(base, sym)) + "</span>" +
        '<span class="mm-cart-gc-label mm-cart-gc-label--end"><b>+' + esc(money(reward, sym)) + " gift card</b><br>" +
        esc(money(target, sym)) + "</span></div>";
    } else {
      var shipSpend = typeof totals.ship === "number" ? totals.ship : spend;
      var shipDone = shipSpend >= shipT;
      var shipAt = Math.round((shipT / threshold) * 1000) / 10;
      tokens.ship_remaining = "<strong>" + esc(money(Math.max(0, shipT - shipSpend), sym)) + "</strong>";

      var msg;
      if (reached) {
        msg = fill(c.reachedText || "", tokens) + " Complimentary shipping included.";
      } else if (shipDone) {
        msg = "🎉 Complimentary shipping unlocked. " + fill(c.belowText || "", tokens);
      } else {
        msg = fill("You're [ship_remaining] from complimentary shipping and [remaining] from a [reward] gift card.", tokens);
      }
      html += '<p class="mm-cart-gc-msg">' + msg + "</p>";

      var size = pct > 0 ? Math.round((10000 / pct) * 10) / 10 : 100;
      html += '<div class="mm-cart-gc-bar">' +
        '<div class="mm-cart-progress-track" role="progressbar"' + aria(spend, threshold, reward, sym) + ">" +
        '<span class="mm-cart-progress-fill" style="width:' + pct + "%;--mm-gc-ship-at:" + shipAt +
        "%;--mm-gc-fill-size:" + size + '% 100%"></span></div>' +
        '<span class="mm-cart-gc-mark" data-done="' + shipDone + '" style="left:' + shipAt + '%" aria-hidden="true"></span>' +
        '<span class="mm-cart-gc-mark mm-cart-gc-mark--end" data-done="' + reached + '" aria-hidden="true"></span>' +
        "</div>";
      html += '<div class="mm-cart-gc-labels" aria-hidden="true">' +
        '<span class="mm-cart-gc-label mm-cart-gc-label--ship" style="--mm-gc-label-at:' + shipAt + '%"><span><b>Shipping</b><br>' +
        esc(money(shipT, sym)) + "</span></span>" +
        '<span class="mm-cart-gc-label mm-cart-gc-label--end"><b>' + esc(money(reward, sym)) + " gift card</b><br>" +
        esc(money(threshold, sym)) + "</span></div>";
    }

    var terms = c.termsText || "";
    var url = c.termsUrl || "";
    if (terms) {
      var t = esc(terms);
      if (url) {
        var link = '<a href="' + esc(url) + '">';
        if (t.indexOf("Terms apply") !== -1) {
          t = t.replace("Terms apply", link + "Terms apply</a>");
        } else {
          t += " " + link + "Terms apply</a>";
        }
      }
      html += '<p class="mm-cart-gc-terms">' + t + "</p>";
    }

    s.setAttribute("data-unlocked", reached ? "true" : "false");
    s.setAttribute("data-combined", combine ? "true" : "false");
    if (s.innerHTML !== html) s.innerHTML = html;
    s.hidden = false;
  }

  function update(state) {
    var c = readConfig();
    if (!c || c.enable !== true || !inWindow(c)) { clear(); return; }
    var t = totalsFromState(state);
    if (t) { lastTotals = t; render(t); return; }
    fetchTotals().then(function (ft) {
      if (ft) lastTotals = ft;
      render(ft);
    });
  }

  /* --- wiring ------------------------------------------------------------------- */

  document.addEventListener("mm:cart:rendered", function (e) {
    update(e && e.detail ? e.detail.state : null);
  });

  /* Opening re-checks the clock, so a drawer left open across the start or
     end of the window corrects itself the next time it is opened. */
  document.addEventListener("mm:cart:open", function (e) {
    var st = e && e.detail ? e.detail.state : null;
    if (st) { update(st); return; }
    if (lastTotals) { render(lastTotals); return; }
    update(null);
  });

  function boot() {
    previewOn(); /* persist the flag from the URL as early as possible */
    var st = window.MMCart && typeof window.MMCart.state === "function" ? window.MMCart.state() : null;
    if (st) { update(st); return; }
    var node = document.querySelector("[data-mm-cart-body] [data-mm-cart-state]");
    if (node) {
      try { update(JSON.parse(node.textContent)); return; } catch (err) { /* fall through */ }
    }
    update(null);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.MMCartPromo = { update: update, preview: previewOn, toEpoch: toEpoch };
}());
